import { CancelToken, HttpClient } from "./HttpClient";

export class TransactionApiClient extends HttpClient {
    constructor() {
        super('https://api.account-tracker.maisiesadler.co.uk')
    }
}

export const TransactionApiV2 = {
    accounts: (cancelToken: CancelToken) =>
        new TransactionApiClient().Get<{ id: string, name: string }[]>('/v2/accounts', cancelToken),
    bank: {
        loginStatus: (cancelToken: CancelToken) =>
            new TransactionApiClient().Get<BankLoginStatusV2>(`/v2/bank/login`, cancelToken),
        summary: (accountId: string, yyyy: string, cancelToken: CancelToken) =>
            new TransactionApiClient().Get<BankLoginSummary>(`/v2/bank/${accountId}/${yyyy}/summary`, cancelToken),
        exchangeCode: (code: string, cancelToken?: CancelToken) =>
            new TransactionApiClient().Post<BankLoginStatusV2>('/v2/bank/login', { code }, cancelToken),
        refresh: (accountId: string, cancelToken?: CancelToken) =>
            new TransactionApiClient().Get<BankLoginStatusV2>(`/v2/bank/${accountId}/refresh`, cancelToken),
        updateTransactions: (accountId: string, reloadYyyyMm: string) =>
            new TransactionApiClient().Get<UpdateTransactionsResponse>(`/v2/bank/${accountId}/transactions?date=${reloadYyyyMm}`),
    },
    reports: {
        get: (accountId: string, yyyyOrYyyyMm: string, reportName: string, cancelToken: CancelToken) =>
            new TransactionApiClient().Get<{ rules: Rule[] }>(`/v2/reports/${accountId}/${yyyyOrYyyyMm}/${reportName}`, cancelToken),
        post: (accountId: string, yyyyOrYyyyMm: string, reportName: string, cancelToken?: CancelToken) =>
            new TransactionApiClient().Post<{ rules: Rule[] }>(`/v2/reports/${accountId}/${yyyyOrYyyyMm}/${reportName}`, {}, cancelToken),
        fudge: {
            put: (request: AddReportFudgeRequest, cancelToken?: CancelToken) =>
                new TransactionApiClient().Put<{ rules: Rule[] }>(`/v2/reports/fudge`, request, cancelToken),
        }
    },
    metadata: {
        post: (accountId: string, request: UpdateTransactionMetadataRequest, cancelToken?: CancelToken) =>
            new TransactionApiClient().Post<{ rules: Rule[] }>(`/v2/metadata`, { accountId, ...request }, cancelToken),
    },
    rules: {
        get: (accountId: string, reportName: string, cancelToken?: CancelToken) =>
            new TransactionApiClient().Get<{ rules: RuleCategoryDefinition[] }>(`/v2/rule/${accountId}/${reportName}`, cancelToken),
        post: (accountId: string, reportName: string, rule: RuleCategoryDefinition, existingCategoryName: string | undefined, cancelToken?: CancelToken) =>
            new TransactionApiClient().Post<{ rules: Rule[] }>(`/v2/rule/${accountId}/${reportName}`, { rule, existingCategoryName }, cancelToken),
    },
}

export interface NewTransaction {
    timestamp: string
    description: string
    amount: number
    source: string
}

export interface Rule {
    type: 'source' | 'description';
    match: string;
    category: string;
}

export interface AccessTokenInfo {
    account_id: string,
    access_token_expiry: string,
}

export interface BankLoginStatusV2 {
    auth_url: string
    accounts: AccessTokenInfo[]
}

export interface BankLoginSummary {
    account: AccessTokenInfo
    transactions: { [mm: string]: { updatedAt: string } }
}

export interface BankLoginStatusV1 {
    auth_url: string
    access_token_expiry: string
}

export interface UpdateTransactionsResponse {
    results: {
        group: string;
        transactions: number;
        updated: number;
    }[]
}

export interface GetAccountsResponse {
    message: string
    results: { accounts: { id: string, name: string }[] }
}

export interface ViewTransactionsResponse {
    account_id: string
    transactions: { [key: string]: Transaction[] }
}

export interface Transaction {
    id: string
    timestamp: string
    description: string
    transactionType: string // CREDIT or DEBIT
    transactionCategory: string
    amount: number // credit transfers are +ve
    source: string
}

export interface GetMappedTransactionsResponse {
    // group
    [key: string]: {
        lastRecalculated: string
        transactions: TransactionsAndCategory[]
        // category: total
        totals: { [key: string]: number }
    }
}

export interface TransactionsAndCategory {
    transaction: Transaction
    category: string
}

export interface AddReportFudgeRequest {
    accountId: string;
    timestamp: string;
    description: string;
    amount: number;
    origin: string;
    reportName: string;
}

export interface RuleCategoryDefinition {
    category: string;
    matches: {
        name: string;
        type?: string;
    }[]
    aliases: {
        name: string
    }[]
}

export interface UpdateTransactionMetadataRequest {
    yyyyMm?: string;
    transactionId?: string;
    fromDate?: string;
    toDate?: string;
    metadataKey: string;
    metadataValue: string;
}
